import type { Frame, ViewEndpoint } from "../protocol/events";
import { projectQuad } from "./framing";

/** One paint per find. Only opacity animates; detector frames never draw an overlay. */
export function mountFindConfirmation(endpoint: ViewEndpoint, camera: HTMLElement): () => void {
  const canvas = document.createElement("canvas");
  canvas.className = "find-confirmation";
  canvas.setAttribute("aria-hidden", "true");
  canvas.hidden = true;
  camera.append(canvas);
  let animation: Animation | null = null;
  const clear = () => {
    animation?.cancel();
    animation = null;
    canvas.hidden = true;
    canvas.width = canvas.height = 0;
  };
  const show = (frame: Frame) => {
    if (!frame.quads[0] || performance.now() - frame.at > 500 || document.hidden) return;
    const width = camera.clientWidth,
      height = camera.clientHeight;
    if (!width || !height) return;
    const quad = projectQuad(frame.quads[0], frame.window, width, height);
    if (quad.some((v) => !Number.isFinite(v))) return;
    clear();
    const ratio = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    const context = canvas.getContext("2d");
    if (!context) return;
    context.scale(ratio, ratio);
    context.beginPath();
    context.moveTo(quad[0], quad[1]);
    for (let i = 2; i < 8; i += 2) context.lineTo(quad[i], quad[i + 1]);
    context.closePath();
    context.fillStyle = "#fff";
    // Paint two blurred shadows, then remove the card interior and its crisp edge.
    for (const [blur, color] of [
      [26, "rgba(109, 235, 178, .65)"],
      [10, "rgba(206, 255, 228, .9)"],
    ] as const) {
      context.shadowColor = color;
      context.shadowBlur = blur * ratio;
      context.fill();
    }
    context.shadowBlur = 0;
    context.globalCompositeOperation = "destination-out";
    context.fill();
    canvas.hidden = false;
    const duration = matchMedia("(prefers-reduced-motion: reduce)").matches ? 150 : 600;
    animation = canvas.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration,
      easing: "ease-out",
      fill: "forwards",
    });
    animation.onfinish = clear;
  };
  endpoint.on("found", (event) => {
    if (event.confirmation) show(event.confirmation);
  });
  endpoint.on("session:stopped", clear);
  endpoint.on("sensor:resized", clear);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) clear();
  });
  return clear;
}
